<template>
  <div class="autocomplete-select">
    <label :for="id" class="form-label">{{ label }}</label>
    <div class="custom-select-wrapper">
      <input
        :id="id"
        type="text"
        class="form-control"
        v-model="searchQuery"
        @click="showOptions = true"
        @keydown.esc="pressEsc"
        @input="filterOptions"
        @blur="handleBlur"
        :placeholder="placeholder"
        autocomplete="off"
        :disabled="disabledSelect"
      />
      <span class="custom-arrow"></span>
    </div>
    <div v-if="showOptions && filteredOptions.length" class="options-container">
      <ul class="list-group mt-1 options-list list-group-flush">
        <li
          class="list-group-item"
          v-for="option in filteredOptions"
          :key="option.value"
          @mousedown.prevent="selectOption(option)"
        >
          <HighlightText :text="option.label" :query="searchQuery"/>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, defineEmits } from 'vue';
import HighlightText from "@/components/base/HighlightText.vue";

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  disabledSelect: {
    type: Boolean,
    default: false,
  },
  modelValue: {
    type: [String, Number, null],
    default: null,
  },
  label: {
    type: String,
    default: 'Select an option',
  },
  placeholder: {
    type: String,
    default: 'Search...',
  },
  id: {
    type: String,
    default: 'autocomplete-select',
  },
});

const emit = defineEmits(['update:modelValue']);

// Reaktivní stav
const searchQuery = ref('');
const showOptions = ref(false);
const filteredOptions = ref([...props.options]);
const selectedLabel = ref('');

// Funkce pro aktualizaci searchQuery podle aktuální modelValue
const updateSearchQueryFromValue = (value) => {
  const found = props.options.find(option => option.value === value);
  if (found) {
    searchQuery.value = found.label;
    selectedLabel.value = found.label;
  } else {
    searchQuery.value = '';
    selectedLabel.value = '';
  }
};

// Logika filtrování
const filterOptions = () => {
  const query = searchQuery.value.toLowerCase();
  filteredOptions.value = props.options.filter(option =>
    option.label.toLowerCase().includes(query)
  );
  showOptions.value = true;
};

const selectOption = (option) => {
  searchQuery.value = option.label;
  selectedLabel.value = option.label;
  emit('update:modelValue', option.value);
  showOptions.value = false;
  filteredOptions.value = [...props.options];
};

const handleBlur = () => {
  // Delay pro zachycení mousedown eventu na položce seznamu
  setTimeout(() => {
    showOptions.value = false;
    // Pokud uživatel nic nevybral a odešel z pole, vrátíme tam původní text nebo smažeme
    if (searchQuery.value !== selectedLabel.value) {
      searchQuery.value = selectedLabel.value;
    }
  }, 200);
};

const pressEsc = () => {
  showOptions.value = false;
  searchQuery.value = selectedLabel.value;
  filteredOptions.value = [...props.options];
};

// Watchery
watch(() => props.options, (newOptions) => {
  filteredOptions.value = [...newOptions];
  updateSearchQueryFromValue(props.modelValue);
}, { deep: true });

watch(() => props.modelValue, (newValue) => {
  updateSearchQueryFromValue(newValue);
});

// Inicializace
onMounted(() => {
  updateSearchQueryFromValue(props.modelValue);
});
</script>

<style scoped>
/* ... styly zůstávají stejné ... */
.autocomplete-select {
  position: relative;

  .custom-select-wrapper {
    position: relative;
  }

  .custom-select-wrapper input {
    padding-right: 30px;
  }

  .custom-arrow {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    pointer-events: none;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #000;
  }

  .options-container {
    position: absolute;
    width: 100%;
    max-height: 315px;
    overflow-y: hidden;
    border: 1px solid #ccc;
    padding: 3px;
    background: #fff;
    z-index: 1000;
  }

  .list-group-item:hover {
    background-color: #f0f0f0;
  }

  .options-list {
    max-height: 315px;
    overflow-y: auto;
    margin: 0;
  }
}
</style>